import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Microscope, Users, Target, ArrowRight, Sparkles, GraduationCap, Layout } from 'lucide-react'
import './Home.css'

// Componente de contador animado
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const countRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true)
        }
      },
      { threshold: 0.5 }
    )

    if (countRef.current) {
      observer.observe(countRef.current)
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current)
      }
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) return

    let startTime = null
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const current = Math.floor(easeOut * end)

      setCount(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setCount(end)
      }
    }

    requestAnimationFrame(animate)
  }, [isVisible, end, duration])

  return (
    <span ref={countRef}>
      {typeof end === 'string' ? end : `${count}${suffix}`}
    </span>
  )
}

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container hero-container">
          <div className="hero-content fade-in-up">
            <div className="hero-badge">
              <Sparkles size={16} />
              <span>Bienvenidos a VTIC</span>
            </div>
            <h1 className="hero-title">
              Impulsando la <br />
              <span className="highlight">Excelencia Científica</span>
            </h1>
            <p className="hero-description">
              Somos el Voluntariado de Educación e Investigación Científica de la UNAH.
              Una comunidad dedicada al avance del conocimiento y el desarrollo profesional.
            </p>
            <div className="hero-buttons">
              <Link to="/secciones" className="btn btn-primary">
                Explorar Materias <ArrowRight size={20} />
              </Link>
              <Link to="/grupos-investigacion" className="btn btn-secondary">
                Investigación
              </Link>
            </div>
          </div>
          <div className="hero-visual fade-in">
            <div className="visual-card">
              <img
                src="https://res.cloudinary.com/dcm2dsjov/image/upload/v1757627650/Agregar_un_subt%C3%ADtulo_10_irdlej.png"
                alt="VTIC Logo"
                className="floating-logo"
              />
              <div className="visual-glow"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Nuestra Propuesta de Valor</h2>
            <div className="section-line"></div>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-wrapper blue">
                <BookOpen size={28} />
              </div>
              <h3>Tutorías Premium</h3>
              <p>Apoyo académico personalizado en ciencias exactas con tutores de alto nivel.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper yellow">
                <Microscope size={28} />
              </div>
              <h3>Investigación Real</h3>
              <p>Oportunidades únicas para integrarte en proyectos científicos desde el pregrado.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper blue">
                <Users size={28} />
              </div>
              <h3>Networking</h3>
              <p>Conecta con una red multidisciplinaria de estudiantes y profesionales apasionados.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper yellow">
                <Target size={28} />
              </div>
              <h3>Liderazgo</h3>
              <p>Desarrolla habilidades blandas y de gestión liderando iniciativas académicas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={6} suffix="+" />
              </div>
              <div className="stat-label">Áreas de Estudio</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={2} />
              </div>
              <div className="stat-label">Grupos Activos</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={150} suffix="+" />
              </div>
              <div className="stat-label">Estudiantes</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={100} suffix="%" />
              </div>
              <div className="stat-label">Pasión por Ciencia</div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Cards */}
      <section className="quick-links-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Explora VTIC</h2>
            <p className="section-subtitle">Accesos directos a lo que necesitas</p>
          </div>

          <div className="quick-links-grid">
            <Link to="/secciones" className="nav-card">
              <div className="nav-card-content">
                <div className="nav-icon"><Layout size={32} /></div>
                <h3>Materias</h3>
                <p>Catálogo de tutorías</p>
              </div>
              <div className="nav-arrow"><ArrowRight size={20} /></div>
            </Link>

            <Link to="/grupos-investigacion" className="nav-card">
              <div className="nav-card-content">
                <div className="nav-icon"><Microscope size={32} /></div>
                <h3>Investigación</h3>
                <p>Grupos y proyectos</p>
              </div>
              <div className="nav-arrow"><ArrowRight size={20} /></div>
            </Link>

            <Link to="/como-ser-tutor" className="nav-card">
              <div className="nav-card-content">
                <div className="nav-icon"><GraduationCap size={32} /></div>
                <h3>Ser Tutor</h3>
                <p>Únete al equipo</p>
              </div>
              <div className="nav-arrow"><ArrowRight size={20} /></div>
            </Link>

            <Link to="/nosotros" className="nav-card">
              <div className="nav-card-content">
                <div className="nav-icon"><Users size={32} /></div>
                <h3>Nosotros</h3>
                <p>Conoce VTIC</p>
              </div>
              <div className="nav-arrow"><ArrowRight size={20} /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Construyamos el futuro de la ciencia</h2>
            <p>Tu potencial es ilimitado. Únete hoy mismo.</p>
            <Link to="/register" className="btn btn-cta">
              Regístrate Ahora
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
